import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppBar from 'app/components/Layout/AppBar';
import Footer from 'app/components/Footer';
import { ParallaxBackground } from 'app/components/ParallaxBackground';
import { SmoothScroll } from 'app/components/SmoothScroll';

export function FAQPage() {
  return (
    <SmoothScroll>
      <div className="relative bg-transparent overflow-hidden">
        <ParallaxBackground particleCount={20} />
        <Helmet>
          <title>FAQ - Sov.ai</title>
          <meta
            name="description"
            content="Frequently asked questions about Sov.ai's institutional-grade alternative datasets and services."
          />
        </Helmet>

        <AppBar />

        <div className="min-h-screen pt-20 pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Everything you need to know about Sov.ai's institutional-grade
                data services.
              </p>
              <div className="mt-6 mx-auto w-24 h-[1px] bg-gradient-to-r from-transparent via-blue-400/50 to-transparent" />
            </div>

            <div className="space-y-12">
              {/* Section: General */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  General
                </h2>
                <div className="space-y-8">
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      What is Sov.ai and what do you offer?
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Sov.ai builds, maintains, and delivers
                      institutional-grade, real-time and historical alternative
                      datasets (equity, sectorial, economic). Our products ship
                      with a Python SDK for programmatic access and are designed
                      for research, forecasting, and production workflows.
                    </p>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      When was the company established?
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      We started building our infrastructure in 2021 and began
                      first client deliveries in 2023.
                    </p>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      How many clients do you have?
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      We do not disclose client counts for confidentiality and
                      contractual reasons.
                    </p>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Do you offer a trial period?
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      Yes. We offer a 14-day trial accessible to everyone.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section: Data & Delivery */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  Data & Delivery
                </h2>
                <div className="space-y-8">
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      How is data delivered to clients?
                    </h3>
                    <div className="text-gray-300 leading-relaxed space-y-4">
                      <div>
                        <strong className="text-blue-400">Formats:</strong>{' '}
                        Parquet by default. CSV, JSON, and Delta formats are
                        available on request.
                      </div>
                      <div>
                        <strong className="text-blue-400">
                          Access Methods:
                        </strong>
                        <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                          <li>
                            Python SDK (
                            <code className="bg-gray-800 px-2 py-1 rounded text-green-400">
                              sov
                            </code>
                            ) for programmatic access
                          </li>
                          <li>
                            Direct bucket access (AWS S3 or GCP Cloud Storage)
                          </li>
                          <li>Snowflake Share</li>
                          <li>Databricks (Tables/Delta)</li>
                          <li>Direct push to your cloud destination</li>
                        </ul>
                      </div>
                      <p>
                        We maintain versioned, partitioned datasets with schemas
                        documented in our data dictionary. For custom delivery,
                        we can configure a client-specific view that omits or
                        masks any named columns.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      How frequently is the data updated?
                    </h3>
                    <div className="text-gray-300 leading-relaxed space-y-4">
                      <p>
                        Data freshness is actively maintained. The update
                        cadence varies by dataset, with specific frequencies and
                        times listed in the data dictionary. Examples include:
                      </p>
                      <ul className="list-disc list-inside ml-4 space-y-2">
                        <li>
                          <strong className="text-blue-400">Daily:</strong>{' '}
                          Equity research signals (e.g., Price Breakout)
                        </li>
                        <li>
                          <strong className="text-blue-400">Weekly:</strong>{' '}
                          Event-driven tables (e.g., Short Selling, Risk
                          Indicators)
                        </li>
                        <li>
                          <strong className="text-blue-400">
                            Event-Driven:
                          </strong>{' '}
                          Macroeconomic data follows source release cycles
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Where can I find a list of all data fields?
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      A complete data dictionary is available with detailed
                      field information, descriptions, types, update
                      frequencies, and other metadata for all datasets.
                    </p>
                    <div className="mt-4 space-y-3">
                      <div>
                        <strong className="text-blue-400">
                          View Online (Google Sheets):
                        </strong>
                        <br />
                        <a
                          href="https://docs.google.com/spreadsheets/d/1MdAeLrB7wiEc8a2-T8ohg41bjSxCG438M6UCLZJ39Bg/edit?usp=sharing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300 transition-colors underline break-all"
                        >
                          https://docs.google.com/spreadsheets/d/1MdAeLrB7wiEc8a2-T8ohg41bjSxCG438M6UCLZJ39Bg/edit?usp=sharing
                        </a>
                      </div>
                      <div>
                        <strong className="text-blue-400">Download CSV:</strong>
                        <br />
                        <a
                          href="https://docs.google.com/spreadsheets/d/e/2PACX-1vS_Gba-DBATmKq5TJn9lWSPP1T1hT1lGE76FJfVqk0byzfUmHA9I8WHUMRlEQuJkIjExVGlS1Fi6Zz6/pub?gid=1076010568&single=true&output=csv"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-400 hover:text-green-300 transition-colors underline break-all"
                        >
                          https://docs.google.com/spreadsheets/d/e/2PACX-1vS_Gba-DBATmKq5TJn9lWSPP1T1hT1lGE76FJfVqk0byzfUmHA9I8WHUMRlEQuJkIjExVGlS1Fi6Zz6/pub?gid=1076010568&single=true&output=csv
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section: Sourcing, Compliance & AI */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  Sourcing, Compliance & AI
                </h2>
                <div className="space-y-8">
                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Where does your data come from?
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      All datasets are derived exclusively from publicly
                      available sources. We do not scrape behind logins or
                      paywalls. Our sources include U.S. government registries
                      (e.g., SEC EDGAR, Senate LDA), public data portals (e.g.,
                      USASpending.gov, CFPB), patent gazettes, and publicly
                      reachable news and information sites like Wikipedia.
                    </p>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Do you use Generative AI (GenAI)?
                    </h3>
                    <div className="text-gray-300 leading-relaxed space-y-3">
                      <p>
                        <strong className="text-blue-400">
                          Yes, for derived annotations only; not for creating
                          primary facts.
                        </strong>
                      </p>
                      <p>
                        We use LLMs and machine learning models to classify
                        topics, map company entities, score sentiment, and
                        standardize noisy text from public records. We do not
                        use AI to invent records or "hallucinate" fundamentals.
                      </p>
                      <p>
                        Every row in our deliverables is traceable to a public
                        source record (e.g., EDGAR accession number, patent ID).
                        All model outputs pass automated validation and quality
                        assurance checks before release.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      What is your webscraping policy?
                    </h3>
                    <div className="text-gray-300 leading-relaxed space-y-3">
                      <p>
                        The vast majority of our data volume comes from official
                        APIs and bulk file downloads provided by regulators
                        (e.g., SEC EDGAR, Senate LDA, CFPB).
                      </p>
                      <p>
                        Webscraping is a minority component focused only on
                        publicly reachable pages like news sites and Wikipedia.
                        We have a strict policy against circumventing access
                        controls:
                      </p>
                      <ul className="list-disc list-inside ml-4 space-y-1">
                        <li>
                          <strong className="text-white">No</strong> scraping
                          behind logins, accounts, or paywalls.
                        </li>
                        <li>
                          <strong className="text-white">No</strong> clicking to
                          accept terms and conditions for automated collection.
                        </li>
                        <li>
                          We respect{' '}
                          <code className="bg-gray-800 px-2 py-1 rounded text-green-400">
                            robots.txt
                          </code>{' '}
                          and use polite rate-limiting.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      How do you handle Personally Identifiable Information
                      (PII) and free-text fields?
                    </h3>
                    <div className="text-gray-300 leading-relaxed space-y-4">
                      <div>
                        <strong className="text-blue-400">
                          Source & Context:
                        </strong>{' '}
                        Our datasets do not contain sensitive PII like Social
                        Security Numbers or personal phone numbers. Names of
                        individuals may appear, but only where they are already
                        public by law or official publication (e.g., registered
                        lobbyists, corporate officers in SEC filings, patent
                        inventors). Free-text fields (like complaint narratives
                        or lobbying descriptions) are taken verbatim from public
                        sources.
                      </div>
                      <div>
                        <strong className="text-blue-400">Safeguards:</strong>{' '}
                        We employ multi-layer safeguards, including upstream
                        controls from the sources themselves (e.g., the CFPB
                        scrubs PII before publication), automated filters to
                        detect and quarantine anomalous data, and quality
                        sampling reviews before every data release.
                      </div>
                      <div>
                        <strong className="text-blue-400">
                          Client Control:
                        </strong>{' '}
                        We can configure a{' '}
                        <strong className="text-white">"no-PII profile"</strong>{' '}
                        for any client. This allows you to exclude any specified
                        fields from your data delivery, including all free-text
                        narratives or contact information columns, ensuring the
                        feed aligns perfectly with your compliance requirements.
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Does your data contain third-party data?
                    </h3>
                    <div className="text-gray-300 leading-relaxed space-y-3">
                      <p>
                        No. Our datasets are built and maintained in-house. We
                        do not license or embed any third-party data feeds in
                        our deliverables.
                      </p>
                      <p>
                        The only exception is an optional, convenience price
                        feed included in our Python SDK for plotting charts.
                        This feed uses publicly redistributable and license-free
                        sources. It can be disabled if you already license your
                        own market data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section: Universe Coverage */}
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8">
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  Universe Coverage
                </h2>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-blue-400 mb-3">
                    Rule of thumb:
                  </h3>
                  <ul className="text-gray-300 space-y-2">
                    <li>
                      <strong className="text-white">
                        Employee Visa, Clinical Trials, Lobbying, Government
                        Contracts, Patents
                      </strong>{' '}
                      ⇒{' '}
                      <span className="text-green-400">
                        US public (listed & delisted) + foreign + private
                      </span>
                      .
                    </li>
                    <li>
                      <strong className="text-white">All other datasets</strong>{' '}
                      ⇒{' '}
                      <span className="text-green-400">
                        US public (listed & delisted)
                      </span>
                      .
                    </li>
                  </ul>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left py-3 px-4 text-blue-400 font-semibold">
                          Category
                        </th>
                        <th className="text-left py-3 px-4 text-blue-400 font-semibold">
                          Dataset
                        </th>
                        <th className="text-left py-3 px-4 text-blue-400 font-semibold">
                          Start
                        </th>
                        <th className="text-left py-3 px-4 text-blue-400 font-semibold">
                          ~Entities
                        </th>
                        <th className="text-left py-3 px-4 text-blue-400 font-semibold">
                          Universe
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-300">
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Accounting Data</td>
                        <td className="py-2 px-4">2012-04-02</td>
                        <td className="py-2 px-4">7,395+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Bankruptcy Predictions</td>
                        <td className="py-2 px-4">1985-01-01</td>
                        <td className="py-2 px-4">2,000+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Employee Visa</td>
                        <td className="py-2 px-4">2001-01-01</td>
                        <td className="py-2 px-4">2,580+</td>
                        <td className="py-2 px-4 text-green-400">
                          US public + foreign + private
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Earnings Surprise</td>
                        <td className="py-2 px-4">2003-09-17</td>
                        <td className="py-2 px-4">2,860+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Congressional Data</td>
                        <td className="py-2 px-4">2011-01-01</td>
                        <td className="py-2 px-4">550+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Factor Signals</td>
                        <td className="py-2 px-4">1992-03-30</td>
                        <td className="py-2 px-4">6,582+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Financial Ratios</td>
                        <td className="py-2 px-4">1996-03-31</td>
                        <td className="py-2 px-4">8,522+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Government Contracts</td>
                        <td className="py-2 px-4">2013-10-01</td>
                        <td className="py-2 px-4">5,900+</td>
                        <td className="py-2 px-4 text-green-400">
                          US public + foreign + private
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Institutional Trading</td>
                        <td className="py-2 px-4">2008-06-30</td>
                        <td className="py-2 px-4">4,760+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Insider Flow Prediction</td>
                        <td className="py-2 px-4">2013-08-15</td>
                        <td className="py-2 px-4">3,700+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">
                          Liquidity — Price Improvement
                        </td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">
                          Liquidity — Market Opportunity
                        </td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Lobbying Data</td>
                        <td className="py-2 px-4">1999-12-31</td>
                        <td className="py-2 px-4">14,500+</td>
                        <td className="py-2 px-4 text-green-400">
                          US public + foreign + private
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">News Sentiment (variants)</td>
                        <td className="py-2 px-4">2017-01-01</td>
                        <td className="py-2 px-4">2,000+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Price Breakout</td>
                        <td className="py-2 px-4">2022-03-10</td>
                        <td className="py-2 px-4">5,400+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Risk Indicators</td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">
                          SEC Edgar Search (tooling)
                        </td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">Beta</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">SEC 10-K Filings</td>
                        <td className="py-2 px-4">1998-03-31</td>
                        <td className="py-2 px-4">4,762+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Short Selling</td>
                        <td className="py-2 px-4">1998-01-02</td>
                        <td className="py-2 px-4">5,981+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Wikipedia Views</td>
                        <td className="py-2 px-4">2015-07-31</td>
                        <td className="py-2 px-4">3,733+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">equity</td>
                        <td className="py-2 px-4">Patents</td>
                        <td className="py-2 px-4">2006-01-12</td>
                        <td className="py-2 px-4">3,200+</td>
                        <td className="py-2 px-4 text-green-400">
                          US public + foreign + private
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">sectorial</td>
                        <td className="py-2 px-4">Box Office Stats</td>
                        <td className="py-2 px-4">1997-07-01</td>
                        <td className="py-2 px-4">100+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr className="border-b border-gray-800/50">
                        <td className="py-2 px-4">sectorial</td>
                        <td className="py-2 px-4">CFPB Complaints</td>
                        <td className="py-2 px-4">2011-12-01</td>
                        <td className="py-2 px-4">1,000+</td>
                        <td className="py-2 px-4">
                          US public (listed+delisted)
                        </td>
                      </tr>
                      <tr>
                        <td className="py-2 px-4">sectorial</td>
                        <td className="py-2 px-4">Pharma Clinical Trials</td>
                        <td className="py-2 px-4">1999-11-01</td>
                        <td className="py-2 px-4">850+</td>
                        <td className="py-2 px-4 text-green-400">
                          US public + foreign + private
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 text-sm text-gray-400">
                  <p className="mb-2">
                    <strong>Notes:</strong>
                  </p>
                  <ul className="space-y-1 ml-4 list-disc">
                    <li>
                      "~Entities" are approximate counts and may grow as
                      coverage expands.
                    </li>
                    <li>
                      All datasets retain delisted names and corporate-action
                      history to avoid survivorship bias.
                    </li>
                    <li>
                      Full field lists and schemas are in the data dictionary.
                    </li>
                  </ul>
                </div>
              </div>

              {/* Section: Commercial & Legal */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  Commercial & Legal
                </h2>
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8 space-y-4 text-gray-300 leading-relaxed">
                  <div>
                    <strong className="text-blue-400">Licensing Model:</strong>{' '}
                    Flexible licensing is available by dataset bundle or as a
                    full platform license. We offer enterprise terms via MSA and
                    provide special pricing for academic and non-profit use
                    cases.
                  </div>
                  <div>
                    <strong className="text-blue-400">
                      Redistribution Rights:
                    </strong>{' '}
                    Internal use within your organization is permitted. External
                    redistribution of our datasets requires prior written
                    consent.
                  </div>
                  <div>
                    <strong className="text-blue-400">Vendor Diligence:</strong>{' '}
                    We routinely complete standard vendor reviews, including
                    InfoSec questionnaires, and sign DPAs/MSAs that align with
                    our data sourcing and privacy policies.
                  </div>
                </div>
              </div>

              {/* Section: Roadmap & Custom Work */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 text-center">
                  Roadmap & Custom Work
                </h2>
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-8 text-gray-300 leading-relaxed">
                  <strong className="text-blue-400">
                    Custom Datasets/Features:
                  </strong>{' '}
                  Yes. We can build custom datasets and features, subject to
                  feasibility and data availability. We prioritize roadmap items
                  based on client demand and offer paid accelerators for bespoke
                  projects.
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </SmoothScroll>
  );
}

export default FAQPage;
